import { v4 as uuid } from 'uuid';
import {
  Lfa_Calendar_Type,
  type Lfa_Conversion_Location_Type,
  type LfaAccountServiceV2FieldsFragment,
  type LfaCalendarV2FieldsFragment,
  type LfaGeneratedCredentialFieldsFragment,
  type LfaServicesV2FieldsFragment,
} from '~/graphql/types';
import computeOfficeChangeset from '~/components/page/Settings/LFASettings/servicesV2/utils/computeOfficeChangeset';
import getDefaultCriteria from '~/components/page/Settings/LFASettings/servicesV2/utils/getDefaultCriteria';

export type ResolvedServiceV2 = Omit<
  LfaAccountServiceV2FieldsFragment,
  '__typename'
>;

export type ServicesV2State = LfaServicesV2FieldsFragment | null;

type CalendarRef =
  LfaServicesV2FieldsFragment['accountServices'][number]['calendars'][number];

export type ServicesV2Action =
  | { type: 'V2_RESET'; payload: ServicesV2State }
  | {
      type: 'V2_INIT_FROM_V1';
      payload: {
        services: Array<{
          id: string;
          locations: Array<{
            type: Lfa_Conversion_Location_Type;
            enabled: boolean;
            note?: string | null;
            weight: number;
          }>;
        }>;
      };
    }
  | { type: 'V2_REMOVE' }
  | { type: 'V2_ADD_CALENDAR'; payload: { id: string } }
  | {
      type: 'V2_UPDATE_CALENDAR';
      payload: { calendarId: string; calendar: LfaCalendarV2FieldsFragment };
    }
  | { type: 'V2_REMOVE_CALENDAR'; payload: { calendarId: string } }
  | { type: 'V2_ADD_CREDENTIAL'; payload: { id: string } }
  | {
      type: 'V2_UPDATE_CREDENTIAL';
      payload: {
        credentialId: string;
        credential: LfaGeneratedCredentialFieldsFragment;
      };
    }
  | { type: 'V2_REMOVE_CREDENTIAL'; payload: { credentialId: string } }
  | {
      type: 'V2_TOGGLE_ACCOUNT_SERVICE';
      payload: { serviceId: string };
    }
  | {
      type: 'V2_SET_SERVICE_LOCATIONS';
      payload: {
        serviceId: string;
        locations: LfaServicesV2FieldsFragment['accountServices'][number]['locations'];
      };
    }
  | {
      type: 'V2_ADD_SERVICE_CRITERION';
      payload: { serviceId: string; text: string };
    }
  | {
      type: 'V2_REMOVE_SERVICE_CRITERION';
      payload: { serviceId: string; criterionId: string };
    }
  | {
      type: 'V2_SET_SERVICE_CALENDARS';
      payload: { serviceId: string; calendars: Array<CalendarRef> };
    }
  | {
      type: 'V2_TOGGLE_SERVICE_LOCATION';
      payload: {
        serviceId: string;
        locationType: Lfa_Conversion_Location_Type;
      };
    }
  | {
      type: 'V2_SET_LOCATION_WEIGHT';
      payload: {
        serviceId: string;
        locationType: Lfa_Conversion_Location_Type;
        weight: number;
      };
    }
  | {
      type: 'V2_SET_LOCATION_NOTE';
      payload: {
        serviceId: string;
        locationType: Lfa_Conversion_Location_Type;
        note: string;
      };
    }
  | {
      type: 'V2_SET_OFFICE_RESOLVED_SERVICES';
      payload: {
        officeId: string;
        resolvedServices: Array<ResolvedServiceV2>;
      };
    };

const servicesV2Reducer = (
  state: ServicesV2State,
  action: ServicesV2Action,
): ServicesV2State => {
  if (action.type === 'V2_RESET') return action.payload;
  if (action.type === 'V2_REMOVE') return null;
  if (action.type === 'V2_INIT_FROM_V1') {
    const accountServices = action.payload.services.map(s => ({
      __typename: 'LFA_Account_Service_V2' as const,
      id: s.id,
      locations: s.locations
        .filter(l => l.enabled)
        .map(l => ({
          __typename: 'LFA_Account_Service_Location_V2' as const,
          type: l.type,
          note: l.note ?? null,
          weight: l.weight,
        })),
      criteria: getDefaultCriteria(s.id),
      calendars: [] as Array<{
        __typename: 'LFA_Service_Calendar_Ref';
        calendarId: string;
        weight: number;
        note?: string | null;
      }>,
    }));
    return {
      __typename: 'LFA_Account_Services_V2' as const,
      credentials: [],
      calendars: [],
      accountServices,
      officeChangesets: [],
    };
  }
  if (!state) return state;

  switch (action.type) {
    case 'V2_ADD_CALENDAR':
      return {
        ...state,
        calendars: [
          ...state.calendars,
          {
            __typename: 'LFA_Calendar_V2' as const,
            id: action.payload.id,
            name: '',
            type: Lfa_Calendar_Type.Unknown,
            note: null,
            link: null,
            credentials: null,
            officeIds: [],
          },
        ],
      };

    case 'V2_UPDATE_CALENDAR':
      return {
        ...state,
        calendars: state.calendars.map(cal =>
          cal.id === action.payload.calendarId ? action.payload.calendar : cal,
        ),
      };

    case 'V2_REMOVE_CALENDAR': {
      const removedId = action.payload.calendarId;
      const stripCalendarRef = <
        T extends { calendars: Array<{ calendarId: string }> },
      >(
        s: T,
      ): T => ({
        ...s,
        calendars: s.calendars.filter(c => c.calendarId !== removedId),
      });

      return {
        ...state,
        calendars: state.calendars.filter(cal => cal.id !== removedId),
        accountServices: state.accountServices.map(stripCalendarRef),
        officeChangesets: state.officeChangesets.map(entry => ({
          ...entry,
          changeset: {
            ...entry.changeset,
            service: entry.changeset.service.map(change => {
              if (change.__typename === 'LFA_Office_Change_Add_V2') {
                return {
                  ...change,
                  addCalendars: change.addCalendars.filter(
                    c => c.calendarId !== removedId,
                  ),
                };
              }
              if (change.__typename === 'LFA_Office_Change_Mutate_V2') {
                return {
                  ...change,
                  mutateAddCalendars: change.mutateAddCalendars?.filter(
                    c => c.calendarId !== removedId,
                  ),
                  removeCalendarIds: change.removeCalendarIds?.filter(
                    id => id !== removedId,
                  ),
                };
              }
              return change;
            }),
          },
        })),
      };
    }

    case 'V2_ADD_CREDENTIAL':
      return {
        ...state,
        credentials: [
          ...state.credentials,
          {
            __typename: 'LFA_Generated_Credential' as const,
            id: action.payload.id,
            name: '',
            username: '',
            password: '',
            note: null,
          },
        ],
      };

    case 'V2_UPDATE_CREDENTIAL':
      return {
        ...state,
        credentials: state.credentials.map(c =>
          c.id === action.payload.credentialId ? action.payload.credential : c,
        ),
      };

    case 'V2_REMOVE_CREDENTIAL':
      return {
        ...state,
        credentials: state.credentials.filter(
          c => c.id !== action.payload.credentialId,
        ),
      };

    case 'V2_TOGGLE_ACCOUNT_SERVICE': {
      const removedServiceId = action.payload.serviceId;
      const exists = state.accountServices.some(s => s.id === removedServiceId);
      if (exists) {
        const cleanedChangesets = state.officeChangesets
          .map(entry => ({
            ...entry,
            changeset: {
              ...entry.changeset,
              service: entry.changeset.service.filter(change => {
                if (
                  change.__typename === 'LFA_Office_Change_Remove_V2' &&
                  change.serviceId === removedServiceId
                )
                  return false;
                if (
                  change.__typename === 'LFA_Office_Change_Mutate_V2' &&
                  change.serviceId === removedServiceId
                )
                  return false;
                return true;
              }),
            },
          }))
          .filter(entry => entry.changeset.service.length > 0);

        return {
          ...state,
          accountServices: state.accountServices.filter(
            s => s.id !== removedServiceId,
          ),
          officeChangesets: cleanedChangesets,
        };
      }
      return {
        ...state,
        accountServices: [
          ...state.accountServices,
          {
            __typename: 'LFA_Account_Service_V2',
            id: action.payload.serviceId,
            locations: [],
            criteria: getDefaultCriteria(action.payload.serviceId),
            calendars: [],
          },
        ],
      };
    }

    case 'V2_TOGGLE_SERVICE_LOCATION': {
      const { serviceId, locationType } = action.payload;
      return {
        ...state,
        accountServices: state.accountServices.map(s => {
          if (s.id !== serviceId) return s;
          const hasLocation = s.locations.some(l => l.type === locationType);
          if (hasLocation && s.locations.length <= 1) return s;
          return {
            ...s,
            locations: hasLocation
              ? s.locations.filter(l => l.type !== locationType)
              : [
                  ...s.locations,
                  {
                    __typename: 'LFA_Account_Service_Location_V2' as const,
                    type: locationType,
                    weight: 0,
                    note: null,
                  },
                ],
          };
        }),
      };
    }

    case 'V2_SET_SERVICE_LOCATIONS':
      return {
        ...state,
        accountServices: state.accountServices.map(s =>
          s.id === action.payload.serviceId
            ? { ...s, locations: action.payload.locations }
            : s,
        ),
      };

    case 'V2_SET_LOCATION_WEIGHT': {
      const { serviceId, locationType, weight } = action.payload;
      return {
        ...state,
        accountServices: state.accountServices.map(s => {
          if (s.id !== serviceId) return s;
          return {
            ...s,
            locations: s.locations.map(l =>
              l.type === locationType ? { ...l, weight } : l,
            ),
          };
        }),
      };
    }

    case 'V2_SET_LOCATION_NOTE': {
      const { serviceId, locationType, note } = action.payload;
      return {
        ...state,
        accountServices: state.accountServices.map(s => {
          if (s.id !== serviceId) return s;
          return {
            ...s,
            locations: s.locations.map(l =>
              l.type === locationType ? { ...l, note } : l,
            ),
          };
        }),
      };
    }

    case 'V2_ADD_SERVICE_CRITERION':
      return {
        ...state,
        accountServices: state.accountServices.map(s => {
          if (s.id !== action.payload.serviceId) return s;
          return {
            ...s,
            criteria: [
              ...s.criteria,
              {
                __typename: 'LFA_Service_Criterion_V2' as const,
                id: uuid(),
                text: action.payload.text,
              },
            ],
          };
        }),
      };

    case 'V2_REMOVE_SERVICE_CRITERION':
      return {
        ...state,
        accountServices: state.accountServices.map(s => {
          if (s.id !== action.payload.serviceId) return s;
          return {
            ...s,
            criteria: s.criteria.filter(
              c => c.id !== action.payload.criterionId,
            ),
          };
        }),
      };

    case 'V2_SET_SERVICE_CALENDARS':
      return {
        ...state,
        accountServices: state.accountServices.map(s =>
          s.id === action.payload.serviceId
            ? { ...s, calendars: action.payload.calendars }
            : s,
        ),
      };

    case 'V2_SET_OFFICE_RESOLVED_SERVICES': {
      const { officeId, resolvedServices } = action.payload;
      const changes = computeOfficeChangeset(
        state.accountServices,
        resolvedServices,
      );

      if (changes.length === 0) {
        return {
          ...state,
          officeChangesets: state.officeChangesets.filter(
            e => e.officeId !== officeId,
          ),
        };
      }

      const existingEntry = state.officeChangesets.find(
        e => e.officeId === officeId,
      );
      if (existingEntry) {
        return {
          ...state,
          officeChangesets: state.officeChangesets.map(e =>
            e.officeId === officeId
              ? {
                  ...e,
                  changeset: { ...e.changeset, service: changes },
                }
              : e,
          ),
        };
      }

      return {
        ...state,
        officeChangesets: [
          ...state.officeChangesets,
          {
            __typename: 'LFA_Office_Changeset_Entry_V2' as const,
            officeId,
            changeset: {
              __typename: 'LFA_Office_Changeset_V2' as const,
              service: changes,
            },
          },
        ],
      };
    }

    default:
      return state;
  }
};

export default servicesV2Reducer;
